import React, { useState, useEffect, useRef } from 'react';
import API from '../services/api';
import jsPDF from 'jspdf';

// Helper to format dates as MM/DD/YYYY without UTC shift
function formatDate(isoString) {
  if (!isoString) return '';
  const datePart = String(isoString).split('T')[0];
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return isoString;
  return `${String(m).padStart(2,'0')}/${String(d).padStart(2,'0')}/${y}`;
}

const BRAND_GREEN = [74, 124, 89];
const PAGE_W = 210;
const MARGIN = 14;
const CONTENT_W = PAGE_W - MARGIN * 2;

async function generateInvoicePDF(clientData, sessions, customLineItems, invoiceNotes, paidStatus, totalFees) {
  // ── load letterhead image ──
  let logoData = null;
  let logoRenderW = 0;
  let logoRenderH = 0;
  try {
    const resp = await fetch('/letterhead.png');
    const blob = await resp.blob();
    logoData = await new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
    // convert natural pixel size to mm at 96dpi, cap at content width
    await new Promise(resolve => {
      const img = new window.Image();
      img.onload = () => {
        const pxToMm = 25.4 / 96;
        const natW = img.naturalWidth * pxToMm;
        const natH = img.naturalHeight * pxToMm;
        if (natW <= CONTENT_W) {
          logoRenderW = natW;
          logoRenderH = natH;
        } else {
          logoRenderW = CONTENT_W;
          logoRenderH = natH * (CONTENT_W / natW);
        }
        resolve();
      };
      img.onerror = resolve;
      img.src = logoData;
    });
  } catch (_) {}

  const doc = new jsPDF();
  const LINE_H = 6;

  // ── header: letterhead centered, or green fallback ──
  let y;
  if (logoData && logoRenderW > 0) {
    const logoX = (PAGE_W - logoRenderW) / 2;
    doc.addImage(logoData, 'PNG', logoX, 8, logoRenderW, logoRenderH);
    y = 8 + logoRenderH + 8;
  } else {
    doc.setFillColor(...BRAND_GREEN);
    doc.rect(0, 0, PAGE_W, 22, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Avenues Unlimited Counseling Center', PAGE_W / 2, 14, { align: 'center' });
    y = 30;
  }

  // ── INVOICE title + optional PAID stamp ──
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('INVOICE', MARGIN, y);

  if (paidStatus) {
    doc.setTextColor(65, 166, 42);
    doc.setDrawColor(65, 166, 42);
    doc.setLineWidth(2);
    doc.roundedRect(138, y - 10, 58, 14, 3, 3);
    doc.setFontSize(16);
    doc.text('PAID', 167, y - 1, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.2);
  }

  y += 4;
  doc.setDrawColor(200, 200, 200);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 8;

  // ── service provider ──
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND_GREEN);
  doc.text('SERVICE PROVIDER', MARGIN, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  ['Avenues Unlimited Counseling Center', '1774 Mellwood Ave., Louisville, KY 40206', 'TIN: 61-1188201  |  Not a network provider'].forEach(line => {
    doc.text(line, MARGIN, y); y += LINE_H;
  });
  y += 3;

  // ── client info ──
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BRAND_GREEN);
  doc.text('CLIENT INFORMATION', MARGIN, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text(`Name: ${clientData.clientName || ''}`, MARGIN, y); y += LINE_H;
  if (clientData.clientDOB) { doc.text(`DOB: ${formatDate(clientData.clientDOB)}`, MARGIN, y); y += LINE_H; }
  if (clientData.gender)    { doc.text(`Gender: ${clientData.gender}`, MARGIN, y); y += LINE_H; }
  y += 4;

  // ── table header ──
  const colX = [MARGIN, 52, 104, 143, 176];
  const colW = [36, 50, 37, 31, 20];
  const headers = ['Date', 'Service', 'Location', 'Diagnosis', 'Amount'];

  doc.setFillColor(240, 242, 240);
  doc.rect(MARGIN, y - 4, CONTENT_W, 8, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...BRAND_GREEN);
  headers.forEach((h, i) => doc.text(h, colX[i] + 1, y + 1));
  doc.setTextColor(0, 0, 0);
  y += 8;

  // ── table rows ──
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const allItems = [
    ...sessions.map(s => [
      formatDate(s.session_date),
      s.service_type || '',
      s.location || 'Office',
      clientData.diagnosisCode || '',
      `$${parseFloat(s.billed_amount || 0).toFixed(2)}`,
    ]),
    ...customLineItems.map(item => [
      formatDate(item.date),
      item.description || '',
      item.location || '',
      item.diagnosis || '',
      `$${parseFloat(item.amount || 0).toFixed(2)}`,
    ]),
  ];

  allItems.forEach((row, rowIdx) => {
    if (y > 274) { doc.addPage(); y = 20; }
    if (rowIdx % 2 === 1) {
      doc.setFillColor(250, 252, 250);
      doc.rect(MARGIN, y - 4, CONTENT_W, LINE_H + 1, 'F');
    }
    row.forEach((val, i) => {
      const lines = doc.splitTextToSize(String(val), colW[i] - 2);
      doc.text(lines[0], colX[i] + 1, y);
    });
    doc.setDrawColor(220, 220, 220);
    doc.line(MARGIN, y + 2, PAGE_W - MARGIN, y + 2);
    y += LINE_H + 1;
  });

  y += 5;

  // ── total ──
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(`Total: $${parseFloat(totalFees || 0).toFixed(2)}`, PAGE_W - MARGIN, y, { align: 'right' });
  y += 10;

  // ── notes ──
  if (invoiceNotes) {
    if (y > 265) { doc.addPage(); y = 20; }
    doc.setFillColor(249, 249, 249);
    doc.rect(MARGIN, y - 3, CONTENT_W, 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Notes:', MARGIN + 2, y + 2);
    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.splitTextToSize(invoiceNotes, CONTENT_W - 4).forEach(ln => {
      if (y > 278) { doc.addPage(); y = 20; }
      doc.text(ln, MARGIN + 2, y); y += LINE_H;
    });
  }

  // ── footers ──
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(160, 160, 160);
    doc.text(
      '1774 Mellwood Ave., Louisville, KY 40206  •  502.459.9635  •  office@avenuesunlimited.com  •  www.AvenuesUnlimited.com',
      PAGE_W / 2, 290, { align: 'center' }
    );
  }

  const safeName = (clientData.clientName || 'Client').replace(/[,\s]+/g, '_');
  const safeDate = new Date().toLocaleDateString('en-US').replace(/\//g, '-');
  return {
    pdfBase64: doc.output('datauristring').split(',')[1],
    fileName: `Invoice_${safeName}_${safeDate}.pdf`,
  };
}

// Practice information constants
const PRACTICE_INFO = {
  name: 'Avenues Unlimited Counseling Center',
  address: '1774 Mellwood Ave.',
  city: 'Louisville',
  state: 'KY',
  zipCode: '40206',
  phone: '502.459.9635',
  email: 'info@AvenuesUnlimited.com',
  website: 'www.AvenuesUnlimited.com',
  networkStatus: 'Not a network provider',
  tin: '61-1188201',
  logoPath: '/images/avenues-logo.png',
};

function AdminInvoicePage() {
  const [allClients, setAllClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClientName, setSelectedClientName] = useState('');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Enhanced client data states - with backward compatibility
  const [clientData, setClientData] = useState({
    clientName: '',
    clientDOB: '',
    clientPhone: '',
    clientEmail: '',
    diagnosisCode: '',
    icd10Code: 'GZ58ZZZ',
    cptCode: '90837',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZipCode: '',
    gender: '',
    reasonForVisit: '',
    emergencyContactName: '',
    emergencyContactPhone: ''
  });

  const [sessions, setSessions] = useState([]);
  const [originalSessions, setOriginalSessions] = useState([]);
  const [invoiceDates, setInvoiceDates] = useState({ start: '', end: '' });

  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [paidStatus, setPaidStatus] = useState(false);
  const [savedInvoices, setSavedInvoices] = useState([]);

  // Email invoice state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // 'success' | 'error' | null

  // Enhanced editing states
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [customLineItems, setCustomLineItems] = useState([]);

  const invoiceRef = useRef(null);

  useEffect(() => {
    fetchAllClients();
    loadSavedInvoices();
  }, []);

  const fetchAllClients = async () => {
    try {
      const res = await API.get('/admin/all-clients');
      setAllClients(res.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      alert('Error fetching all clients');
    }
  };

  const loadSavedInvoices = () => {
    try {
      const saved = localStorage.getItem('savedInvoices');
      if (saved) {
        setSavedInvoices(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading saved invoices:', error);
      setSavedInvoices([]);
    }
  };

  const displayedClients = searchTerm.trim()
    ? allClients.filter((c) =>
        c.client_name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allClients;

  const handleSelectClient = (client) => {
    setSelectedClientId(client.id);
    setSelectedClientName(client.client_name);
    setSearchTerm(client.client_name);
    clearInvoiceData();
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    clearInvoiceData();
    setSelectedClientId(null);
    setSelectedClientName('');
  };

  const clearInvoiceData = () => {
    setClientData({
      clientName: '',
      clientDOB: '',
      clientPhone: '',
      clientEmail: '',
      diagnosisCode: '',
      icd10Code: 'GZ58ZZZ',
      cptCode: '90837',
      billingAddress: '',
      billingCity: '',
      billingState: '',
      billingZipCode: '',
      gender: '',
      reasonForVisit: '',
      emergencyContactName: '',
      emergencyContactPhone: ''
    });
    setSessions([]);
    setOriginalSessions([]);
    setInvoiceDates({ start: '', end: '' });
    setPaidStatus(false);
    setInvoiceNotes('');
    setCustomLineItems([]);
  };

  const fetchInvoice = async () => {
    if (!selectedClientId || !startDate || !endDate) {
      alert('Please select a client and date range.');
      return;
    }
    setLoading(true);
    try {
      const res = await API.get('/admin/client-invoice', {
        params: {
          clientId: selectedClientId,
          startDate,
          endDate,
        },
      });

      console.log('Invoice response:', res.data); // Debug log

      // Enhanced client data population with backward compatibility
      const responseData = res.data || {};
      
      const newClientData = {
        // Try multiple possible field names for backward compatibility
        clientName: responseData.clientName || responseData.client_name || '',
        clientDOB: responseData.dateOfBirth || responseData.dob || responseData.client_dob || '',
        clientPhone: responseData.clientPhone || responseData.phone || responseData.client_phone || '',
        clientEmail: responseData.clientEmail || responseData.email || responseData.client_email || '',
        diagnosisCode: responseData.diagnosisCode || responseData.diagnosis_code || '',
        icd10Code: responseData.icd10Code || responseData.icd_10_code || 'GZ58ZZZ',
        cptCode: responseData.cptCode || responseData.cpt_code || '90837',
        billingAddress: responseData.billingAddress || responseData.billing_address || '',
        billingCity: responseData.city || responseData.billing_city || '',
        billingState: responseData.state || responseData.billing_state || '',
        billingZipCode: responseData.zip_code || responseData.billing_zip_code || '',
        gender: responseData.gender || '',
        reasonForVisit: responseData.reasonForVisit || responseData.reason_for_visit || '',
        emergencyContactName: responseData.emergencyContactName || responseData.emergency_contact_name || '',
        emergencyContactPhone: responseData.emergencyContactPhone || responseData.emergency_contact_phone || ''
      };

      setClientData(newClientData);
      
      const sessionData = responseData.sessions || [];
      setSessions(sessionData);
      setOriginalSessions([...sessionData]);
      setInvoiceDates({
        start: responseData.startDate || startDate,
        end: responseData.endDate || endDate,
      });
      setPaidStatus(responseData.isPaid || false);

      // Load any saved customizations if they exist
      if (responseData.customizations) {
        setInvoiceNotes(responseData.customizations.customNotes || '');
        setCustomLineItems(responseData.customizations.customLineItems || []);
        setPaidStatus(responseData.customizations.isPaid || false);
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
      
      // More detailed error handling
      if (error.response) {
        console.error('Error response:', error.response.data);
        alert(`Error fetching invoice: ${error.response.data.message || 'Server error'}`);
      } else if (error.request) {
        alert('Error fetching invoice: No response from server');
      } else {
        alert('Error fetching invoice: Request failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClientDataChange = (field, value) => {
    setClientData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSessionEdit = (sessionIndex, field, value) => {
    const updatedSessions = [...sessions];
    updatedSessions[sessionIndex] = {
      ...updatedSessions[sessionIndex],
      [field]: value
    };
    setSessions(updatedSessions);
  };

  const removeSession = (sessionIndex) => {
    if (window.confirm('Are you sure you want to remove this session from the invoice?')) {
      const updatedSessions = sessions.filter((_, index) => index !== sessionIndex);
      setSessions(updatedSessions);
    }
  };

  const addCustomLineItem = () => {
    const newItem = {
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      description: 'Custom Service',
      location: 'Office',
      diagnosis: clientData.diagnosisCode || '',
      amount: 0
    };
    setCustomLineItems([...customLineItems, newItem]);
  };

  const updateCustomLineItem = (itemId, field, value) => {
    setCustomLineItems(items =>
      items.map(item =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    );
  };

  const removeCustomLineItem = (itemId) => {
    setCustomLineItems(items => items.filter(item => item.id !== itemId));
  };

  const duplicateSession = (sessionIndex) => {
    const sessionToDuplicate = { ...sessions[sessionIndex] };
    const updatedSessions = [...sessions];
    updatedSessions.splice(sessionIndex + 1, 0, sessionToDuplicate);
    setSessions(updatedSessions);
  };

  const resetSessions = () => {
    if (window.confirm('Reset all changes to original session data?')) {
      setSessions([...originalSessions]);
      setCustomLineItems([]);
      setInvoiceNotes('');
      setClientData(prev => ({
        ...prev,
        // Reset to original values but keep what we fetched
      }));
    }
  };

  const saveInvoiceTemplate = () => {
    try {
      const template = {
        id: Date.now(),
        clientId: selectedClientId,
        clientName: selectedClientName,
        dateRange: `${startDate} to ${endDate}`,
        clientData: { ...clientData },
        sessions: [...sessions],
        customLineItems: [...customLineItems],
        invoiceNotes,
        paidStatus,
        savedAt: new Date().toISOString()
      };

      const updated = [...savedInvoices, template];
      setSavedInvoices(updated);
      localStorage.setItem('savedInvoices', JSON.stringify(updated));
      alert('Invoice template saved successfully!');
    } catch (error) {
      console.error('Error saving invoice template:', error);
      alert('Error saving invoice template');
    }
  };

  const loadInvoiceTemplate = (template) => {
    if (window.confirm('Load this saved invoice? Current changes will be lost.')) {
      setSelectedClientId(template.clientId);
      setSelectedClientName(template.clientName);
      setClientData(template.clientData);
      setSessions(template.sessions);
      setCustomLineItems(template.customLineItems || []);
      setInvoiceNotes(template.invoiceNotes || '');
      setPaidStatus(template.paidStatus || false);
      
      // Extract dates from saved template
      const dates = template.dateRange.split(' to ');
      if (dates.length === 2) {
        setStartDate(dates[0]);
        setEndDate(dates[1]);
      }
    }
  };

  const deleteInvoiceTemplate = (templateId) => {
    if (window.confirm('Delete this saved invoice template?')) {
      const updated = savedInvoices.filter(t => t.id !== templateId);
      setSavedInvoices(updated);
      localStorage.setItem('savedInvoices', JSON.stringify(updated));
    }
  };

  // Calculate totals including custom line items
  const allLineItems = [
    ...sessions.map(s => ({ amount: parseFloat(s.billed_amount) || 0 })),
    ...customLineItems.map(item => ({ amount: parseFloat(item.amount) || 0 }))
  ];
  const totalFees = allLineItems.reduce((sum, item) => sum + item.amount, 0);

  const exportToCSV = () => {
    if (!sessions.length && !customLineItems.length) return;

    let csvContent = 'data:text/csv;charset=utf-8,';
    csvContent += 'Date,Service,Location,Diagnosis,Amount\n';

    sessions.forEach((s) => {
      const row = [
        formatDate(s.session_date),
        `"${s.service_type || ''}"`,
        `"${s.location || 'Office'}"`,
        `"${clientData.diagnosisCode || ''}"`,
        s.billed_amount || 0,
      ];
      csvContent += row.join(',') + '\n';
    });

    customLineItems.forEach((item) => {
      const row = [
        formatDate(item.date),
        `"${item.description || ''}"`,
        `"${item.location || ''}"`,
        `"${item.diagnosis || ''}"`,
        item.amount || 0,
      ];
      csvContent += row.join(',') + '\n';
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Invoice_${(clientData.clientName || 'Unknown').replace(/\s/g, '_')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    if (!sessions.length && !customLineItems.length) return;

    const newWin = window.open('', '_blank', 'width=800,height=900');
    if (!newWin) {
      alert('Failed to open new window for PDF export.');
      return;
    }

    newWin.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - ${clientData.clientName || 'Unknown Client'}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.5; }
          .header, .footer { text-align: center; margin-bottom: 20px; }
          .header img, .footer img { max-width: 100%; height: auto; }
          .paid-stamp {
            position: absolute; top: 200px; right: 100px; transform: rotate(15deg);
            color: #41a62a; font-size: 80px; font-weight: bold; border: 10px solid #41a62a;
            border-radius: 10px; padding: 10px 20px; opacity: 0.8;
          }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid var(--border-color); padding: 8px; text-align: left; }
          th { background-color: #f2f2f2; }
          .service-provider, .client-info { margin-bottom: 20px; }
          .total { font-weight: bold; text-align: right; margin-top: 20px; }
          .contact-info { font-size: 12px; text-align: center; margin-top: 30px; color: var(--text-strong); }
          .notes-section { margin-top: 20px; padding: 10px; background-color: var(--surface-muted); border-radius: 4px; }
          @media print { body { margin: 0; padding: 20px; } .no-print { display: none; } }
        </style>
      </head>
      <body>
    `);

    newWin.document.write(`
      <div class="header">
        <img src="${PRACTICE_INFO.logoPath}" alt="${PRACTICE_INFO.name}" 
             onerror="this.onerror=null; this.src='https://www.avenuesportal.com/letterhead.png';" />
      </div>
      
      ${paidStatus ? '<div class="paid-stamp">PAID</div>' : ''}

      <div class="service-provider">
        <h3>Service Provider:</h3>
        <div>${PRACTICE_INFO.name}</div>
        <div>${PRACTICE_INFO.address}</div>
        <div>${PRACTICE_INFO.city}, ${PRACTICE_INFO.state} ${PRACTICE_INFO.zipCode}</div>
        <div>${PRACTICE_INFO.networkStatus}</div>
        <div>TIN: ${PRACTICE_INFO.tin}</div>
      </div>

      <div class="client-info">
        <div><strong>Client Name:</strong> ${clientData.clientName || 'Unknown'}</div>
        <div><strong>DOB:</strong> ${clientData.clientDOB ? formatDate(clientData.clientDOB) : 'Not provided'}</div>
        ${clientData.gender ? `<div><strong>Gender:</strong> ${clientData.gender}</div>` : ''}
      </div>

      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Service</th>
            <th>Location</th>
            <th>Diagnosis</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
    `);

    // Add sessions
    sessions.forEach(session => {
      newWin.document.write(`
        <tr>
          <td>${formatDate(session.session_date)}</td>
          <td>${session.service_type || ''}</td>
          <td>${session.location || 'Office'}</td>
          <td>${clientData.diagnosisCode || ''}</td>
          <td>$${parseFloat(session.billed_amount || 0).toFixed(2)}</td>
        </tr>
      `);
    });

    // Add custom line items
    customLineItems.forEach(item => {
      newWin.document.write(`
        <tr>
          <td>${formatDate(item.date)}</td>
          <td>${item.description || ''}</td>
          <td>${item.location || ''}</td>
          <td>${item.diagnosis || ''}</td>
          <td>$${parseFloat(item.amount || 0).toFixed(2)}</td>
        </tr>
      `);
    });

    newWin.document.write(`
        </tbody>
      </table>

      <div class="total">
        Total: $${totalFees.toFixed(2)}
      </div>

      ${invoiceNotes ? `
        <div class="notes-section">
          <strong>Notes:</strong><br>
          ${invoiceNotes.replace(/\n/g, '<br>')}
        </div>
      ` : ''}

      <div class="contact-info">
        ${PRACTICE_INFO.address} | ${PRACTICE_INFO.city}, ${PRACTICE_INFO.state} ${PRACTICE_INFO.zipCode} | 
        ${PRACTICE_INFO.phone} | ${PRACTICE_INFO.email} | ${PRACTICE_INFO.website}
      </div>

      <script>
        // Close the window only after the print/save dialog finishes, so it
        // doesn't vanish mid-"Save as PDF".
        window.onafterprint = function() { window.close(); };
        window.onload = function() {
          setTimeout(function() {
            window.focus();
            window.print();
          }, 400);
        };
      </script>
      </body>
      </html>
    `);

    newWin.document.close();
  };

  const handleOpenEmailModal = () => {
    if (!sessions.length && !customLineItems.length) {
      alert('Please generate an invoice first.');
      return;
    }
    setEmailTo(clientData.clientEmail || clientData.billingEmail || '');
    setEmailMessage('');
    setEmailStatus(null);
    setShowEmailModal(true);
  };

  const handleSendInvoiceEmail = async () => {
    if (!emailTo) { alert('Please enter a recipient email address.'); return; }
    setEmailSending(true);
    setEmailStatus(null);
    try {
      const { pdfBase64, fileName } = await generateInvoicePDF(
        clientData, sessions, customLineItems, invoiceNotes, paidStatus, totalFees
      );
      await API.post('/admin/send-invoice-email', {
        recipientEmail: emailTo,
        clientData,
        emailMessage,
        pdfBase64,
        fileName,
      });
      setEmailStatus('success');
    } catch (err) {
      console.error('Invoice email error:', err);
      setEmailStatus('error');
    } finally {
      setEmailSending(false);
    }
  };

  const toggleEditing = () => {
    setIsEditing(!isEditing);
  };

  const togglePaidStatus = () => {
    setPaidStatus(!paidStatus);
  };

  return (
    <div style={{ margin: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Enhanced Invoice Generator</h2>
        <div>
          <button 
            onClick={saveInvoiceTemplate} 
            style={{ 
              padding: '8px 16px', 
              backgroundColor: 'var(--success-color)', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              marginRight: '10px',
              cursor: 'pointer'
            }}
            disabled={!sessions.length}
          >
            💾 Save Template
          </button>
        </div>
      </div>

      {/* Saved Invoice Templates */}
      {savedInvoices.length > 0 && (
        <div style={{ 
          backgroundColor: 'var(--surface-muted)', 
          padding: '15px', 
          borderRadius: '6px', 
          marginBottom: '20px',
          border: '1px solid var(--border-color)'
        }}>
          <h3>Saved Invoice Templates</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '10px' }}>
            {savedInvoices.map(template => (
              <div key={template.id} style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '10px', 
                border: '1px solid var(--border-color)', 
                borderRadius: '4px', 
                backgroundColor: '#fff'
              }}>
                <div>
                  <strong>{template.clientName}</strong>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{template.dateRange}</div>
                  <small>{new Date(template.savedAt).toLocaleDateString()}</small>
                </div>
                <div>
                  <button 
                    onClick={() => loadInvoiceTemplate(template)}
                    style={{ 
                      padding: '4px 8px', 
                      backgroundColor: 'var(--accent-color)', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '3px', 
                      marginRight: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    Load
                  </button>
                  <button 
                    onClick={() => deleteInvoiceTemplate(template.id)}
                    style={{ 
                      padding: '4px 8px', 
                      backgroundColor: 'var(--error-color)', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '3px',
                      cursor: 'pointer'
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Debug Information - Remove in production */}
      {loading && (
        <div style={{ 
          padding: '10px', 
          backgroundColor: '#d1ecf1', 
          border: '1px solid #bee5eb', 
          borderRadius: '4px', 
          marginBottom: '20px' 
        }}>
          Loading invoice data...
        </div>
      )}

      {/* SEARCH + LIST WRAPPER */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
        {/* LEFT: SEARCH INPUT */}
        <div>
          <label>Search Client:</label>
          <br />
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Type to search by client name..."
            style={{ width: '200px', padding: '8px', marginBottom: '10px' }}
          />
          {selectedClientName && (
            <div style={{ marginTop: '0.5rem' }}>
              <strong>Selected Client:</strong> {selectedClientName}
            </div>
          )}
        </div>

        {/* RIGHT: BOX OF CLIENTS */}
        <div
          style={{
            border: '1px solid var(--border-color)',
            width: '220px',
            height: '250px',
            overflowY: 'auto',
            padding: '0.5rem',
          }}
        >
          {displayedClients.length === 0 ? (
            <div style={{ color: '#999' }}>No clients found</div>
          ) : (
            displayedClients.map((client) => (
              <div
                key={client.id}
                style={{
                  cursor: 'pointer',
                  borderBottom: '1px solid var(--border-color)',
                  padding: '0.25rem 0',
                  backgroundColor: selectedClientId === client.id ? '#e6f7ff' : 'transparent'
                }}
                onClick={() => handleSelectClient(client)}
              >
                {client.client_name}
              </div>
            ))
          )}
        </div>
      </div>

      {/* DATE RANGE */}
      <div style={{ marginTop: '1rem' }}>
        <label>Start Date:</label>
        <br />
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          required
          style={{ padding: '8px', marginRight: '10px' }}
        />
        <label>End Date:</label>
        <br />
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          required
          style={{ padding: '8px' }}
        />
      </div>

      {/* PREVIEW BUTTON */}
      <button 
        onClick={fetchInvoice} 
        style={{ 
          margin: '1rem 0', 
          padding: '10px 20px', 
          backgroundColor: 'var(--accent-color)', 
          color: 'white', 
          border: 'none', 
          borderRadius: '4px',
          cursor: 'pointer'
        }}
        disabled={loading}
      >
        {loading ? 'Loading...' : 'Generate Invoice'}
      </button>

      {sessions.length > 0 && (
        <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
          <label style={{ marginRight: '15px' }}>
            <input
              type="checkbox"
              checked={isEditing}
              onChange={toggleEditing}
              style={{ marginRight: '5px' }}
            />
            Enable Editing
          </label>
          <label style={{ marginRight: '15px' }}>
            <input
              type="checkbox"
              checked={paidStatus}
              onChange={togglePaidStatus}
              style={{ marginRight: '5px' }}
            />
            Mark as Paid
          </label>
          
          {isEditing && (
            <>
              <button
                onClick={addCustomLineItem}
                style={{ 
                  padding: '6px 12px', 
                  backgroundColor: 'var(--success-color)', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  marginRight: '10px',
                  cursor: 'pointer'
                }}
              >
                + Add Custom Item
              </button>
              <button
                onClick={resetSessions}
                style={{ 
                  padding: '6px 12px', 
                  backgroundColor: 'var(--accent-color)', 
                  color: '#000', 
                  border: 'none', 
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Reset Changes
              </button>
            </>
          )}
        </div>
      )}

      {/* Enhanced Client Info Section */}
      {isEditing && sessions.length > 0 && (
        <div style={{ 
          backgroundColor: '#fff8dc', 
          padding: '15px', 
          borderRadius: '6px', 
          marginBottom: '20px',
          border: '1px solid #f0e68c'
        }}>
          <h3>Client Information (Editable)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
            <div>
              <label>Client Name:</label>
              <input
                type="text"
                value={clientData.clientName}
                onChange={(e) => handleClientDataChange('clientName', e.target.value)}
                style={{ width: '100%', padding: '6px', marginTop: '3px' }}
              />
            </div>
            <div>
              <label>Date of Birth:</label>
              <input
                type="date"
                value={clientData.clientDOB}
                onChange={(e) => handleClientDataChange('clientDOB', e.target.value)}
                style={{ width: '100%', padding: '6px', marginTop: '3px' }}
              />
            </div>
            <div>
              <label>Phone:</label>
              <input
                type="text"
                value={clientData.clientPhone}
                onChange={(e) => handleClientDataChange('clientPhone', e.target.value)}
                style={{ width: '100%', padding: '6px', marginTop: '3px' }}
              />
            </div>
            <div>
              <label>Email:</label>
              <input
                type="email"
                value={clientData.clientEmail}
                onChange={(e) => handleClientDataChange('clientEmail', e.target.value)}
                style={{ width: '100%', padding: '6px', marginTop: '3px' }}
              />
            </div>
            <div>
              <label>Diagnosis Code:</label>
              <input
                type="text"
                value={clientData.diagnosisCode}
                onChange={(e) => handleClientDataChange('diagnosisCode', e.target.value)}
                style={{ width: '100%', padding: '6px', marginTop: '3px' }}
              />
            </div>
            <div>
              <label>ICD10 Code:</label>
              <input
                type="text"
                value={clientData.icd10Code}
                onChange={(e) => handleClientDataChange('icd10Code', e.target.value)}
                style={{ width: '100%', padding: '6px', marginTop: '3px' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* INVOICE DISPLAY */}
      {sessions.length > 0 && (
        <div
          ref={invoiceRef}
          style={{
            marginTop: '2rem',
            border: '1px solid var(--border-color)',
            padding: '1rem',
            position: 'relative',
            backgroundColor: '#fff'
          }}
        >
          {/* HEADER LOGO */}
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <img
              src={PRACTICE_INFO.logoPath}
              alt={PRACTICE_INFO.name}
              style={{ maxWidth: '100%', height: 'auto' }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://www.avenuesportal.com/letterhead.png';
              }}
            />
          </div>

          {/* PAID STAMP (if marked as paid) */}
          {paidStatus && (
            <div
              style={{
                position: 'absolute',
                top: '150px',
                right: '60px',
                transform: 'rotate(15deg)',
                color: '#41a62a',
                fontSize: '60px',
                fontWeight: 'bold',
                border: '8px solid #41a62a',
                borderRadius: '10px',
                padding: '5px 15px',
                opacity: '0.8',
              }}
            >
              PAID
            </div>
          )}

          {/* SERVICE PROVIDER INFO */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h3>Service Provider:</h3>
            <div>{PRACTICE_INFO.name}</div>
            <div>{PRACTICE_INFO.address}</div>
            <div>
              {PRACTICE_INFO.city}, {PRACTICE_INFO.state} {PRACTICE_INFO.zipCode}
            </div>
            <div>{PRACTICE_INFO.networkStatus}</div>
            <div>TIN: {PRACTICE_INFO.tin}</div>
          </div>

          {/* CLIENT INFO - REMOVED PHONE AND REASON FOR VISIT */}
          <div style={{ marginBottom: '1.5rem', padding: '10px', backgroundColor: 'var(--surface-muted)', borderRadius: '4px' }}>
            <div><strong>Client Name:</strong> {clientData.clientName}</div>
            <div><strong>DOB:</strong> {clientData.clientDOB ? formatDate(clientData.clientDOB) : 'Not provided'}</div>
            {clientData.gender && <div><strong>Gender:</strong> {clientData.gender}</div>}
          </div>

          {/* TABLE OF SESSIONS */}
          <table
            border="1"
            cellPadding="8"
            style={{ borderCollapse: 'collapse', width: '100%' }}
          >
            <thead>
              <tr>
                <th>Date</th>
                <th>Service</th>
                <th>Location</th>
                <th>Diagnosis</th>
                <th>Amount</th>
                {isEditing && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {sessions.length === 0 ? (
                <tr>
                  <td colSpan={isEditing ? "6" : "5"} style={{ textAlign: 'center' }}>
                    No sessions found
                  </td>
                </tr>
              ) : (
                sessions.map((s, index) => (
                  <tr key={index}>
                    <td>
                      {isEditing ? (
                        <input
                          type="date"
                          value={s.session_date ? s.session_date.split('T')[0] : ''}
                          onChange={(e) => handleSessionEdit(index, 'session_date', e.target.value)}
                          style={{ width: '100%', padding: '2px' }}
                        />
                      ) : (
                        formatDate(s.session_date)
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <input
                          type="text"
                          value={s.service_type || ''}
                          onChange={(e) => handleSessionEdit(index, 'service_type', e.target.value)}
                          style={{ width: '100%', padding: '2px' }}
                        />
                      ) : (
                        s.service_type || ''
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <input
                          type="text"
                          value={s.location || 'Office'}
                          onChange={(e) => handleSessionEdit(index, 'location', e.target.value)}
                          style={{ width: '100%', padding: '2px' }}
                        />
                      ) : (
                        s.location || 'Office'
                      )}
                    </td>
                    <td>{clientData.diagnosisCode}</td>
                    <td>
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.01"
                          value={s.billed_amount || 0}
                          onChange={(e) => handleSessionEdit(index, 'billed_amount', e.target.value)}
                          style={{ width: '100%', padding: '2px' }}
                        />
                      ) : (
                        `$${parseFloat(s.billed_amount || 0).toFixed(2)}`
                      )}
                    </td>
                    {isEditing && (
                      <td>
                        <button
                          onClick={() => duplicateSession(index)}
                          style={{ 
                            marginRight: '5px', 
                            padding: '2px 6px', 
                            backgroundColor: '#17a2b8', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '3px',
                            cursor: 'pointer'
                          }}
                          title="Duplicate"
                        >
                          📋
                        </button>
                        <button
                          onClick={() => removeSession(index)}
                          style={{ 
                            padding: '2px 6px', 
                            backgroundColor: 'var(--error-color)', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '3px',
                            cursor: 'pointer'
                          }}
                          title="Remove"
                        >
                          🗑️
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}

              {/* Custom Line Items */}
              {customLineItems.map((item) => (
                <tr key={item.id} style={{ backgroundColor: '#fff3cd' }}>
                  <td>
                    <input
                      type="date"
                      value={item.date}
                      onChange={(e) => updateCustomLineItem(item.id, 'date', e.target.value)}
                      style={{ width: '100%', padding: '2px' }}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => updateCustomLineItem(item.id, 'description', e.target.value)}
                      style={{ width: '100%', padding: '2px' }}
                      placeholder="Service description"
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={item.location}
                      onChange={(e) => updateCustomLineItem(item.id, 'location', e.target.value)}
                      style={{ width: '100%', padding: '2px' }}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={item.diagnosis}
                      onChange={(e) => updateCustomLineItem(item.id, 'diagnosis', e.target.value)}
                      style={{ width: '100%', padding: '2px' }}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.01"
                      value={item.amount}
                      onChange={(e) => updateCustomLineItem(item.id, 'amount', e.target.value)}
                      style={{ width: '100%', padding: '2px' }}
                    />
                  </td>
                  {isEditing && (
                    <td>
                      <button
                        onClick={() => removeCustomLineItem(item.id)}
                        style={{ 
                          padding: '2px 6px', 
                          backgroundColor: 'var(--error-color)', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                        title="Remove Custom Item"
                      >
                        🗑️
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ textAlign: 'right', marginTop: '1rem', fontWeight: 'bold' }}>
            Total: ${totalFees.toFixed(2)}
          </div>

          {/* Invoice Notes */}
          {(isEditing || invoiceNotes) && (
            <div style={{ marginTop: '20px', padding: '10px', backgroundColor: 'var(--surface-muted)', borderRadius: '4px' }}>
              <h4>Invoice Notes:</h4>
              {isEditing ? (
                <textarea
                  value={invoiceNotes}
                  onChange={(e) => setInvoiceNotes(e.target.value)}
                  style={{ width: '100%', minHeight: '80px', padding: '8px' }}
                  placeholder="Add any special notes or instructions..."
                />
              ) : (
                <div style={{ whiteSpace: 'pre-wrap' }}>{invoiceNotes}</div>
              )}
            </div>
          )}

          {/* CONTACT INFO FOOTER */}
          <div
            style={{
              fontSize: '12px',
              textAlign: 'center',
              marginTop: '2rem',
              color: 'var(--text-strong)',
            }}
          >
            {PRACTICE_INFO.address} | {PRACTICE_INFO.city}, {PRACTICE_INFO.state}{' '}
            {PRACTICE_INFO.zipCode} | {PRACTICE_INFO.phone} |{' '}
            {PRACTICE_INFO.email} | {PRACTICE_INFO.website}
          </div>
        </div>
      )}

      {/* EXPORT BUTTONS */}
      {sessions.length > 0 && (
        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <button 
            onClick={exportToCSV} 
            style={{ 
              marginRight: '1rem', 
              padding: '10px 20px', 
              backgroundColor: 'var(--success-color)', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            📊 Export to Excel (CSV)
          </button>
          <button
            onClick={exportToPDF}
            style={{
              padding: '10px 20px',
              backgroundColor: 'var(--error-color)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            📄 Export to PDF
          </button>
          <button
            onClick={handleOpenEmailModal}
            style={{
              marginLeft: '1rem',
              padding: '10px 20px',
              backgroundColor: '#1890ff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            ✉️ Email Invoice
          </button>
        </div>
      )}

      {/* Email Invoice Modal */}
      {showEmailModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '24px', width: '960px', maxWidth: '96vw', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>Email Invoice to Client</h3>
              <button onClick={() => setShowEmailModal(false)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer' }}>×</button>
            </div>

            {emailStatus === 'success' ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <div style={{ fontSize: 48 }}>✅</div>
                <p style={{ color: 'var(--accent-color)', fontWeight: 700, fontSize: 18, marginTop: 12 }}>Invoice sent successfully!</p>
                <p style={{ color: 'var(--text-strong)' }}>Emailed to <strong>{emailTo}</strong>.</p>
                <button onClick={() => setShowEmailModal(false)} style={{ marginTop: 16, padding: '10px 28px', backgroundColor: 'var(--accent-color)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: 15 }}>Done</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>

                {/* Compose panel */}
                <div style={{ flex: '0 0 300px', minWidth: '240px', width: '100%' }}>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4 }}>To</label>
                    <input
                      type="email"
                      value={emailTo}
                      onChange={e => setEmailTo(e.target.value)}
                      placeholder="client@example.com"
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                    />
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Message (optional)</label>
                    <textarea
                      value={emailMessage}
                      onChange={e => setEmailMessage(e.target.value)}
                      rows={5}
                      placeholder="Add a personal note to the client..."
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 12px 0' }}>
                    {sessions.length + customLineItems.length} line item(s) &bull; Total: <strong>${totalFees.toFixed(2)}</strong>
                  </p>
                  {emailStatus === 'error' && (
                    <p style={{ color: 'var(--error-color)', fontSize: 13, margin: '0 0 10px 0' }}>Failed to send. Please try again.</p>
                  )}
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      onClick={handleSendInvoiceEmail}
                      disabled={emailSending || !emailTo}
                      style={{ padding: '9px 22px', backgroundColor: 'var(--accent-color)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, opacity: emailSending || !emailTo ? 0.6 : 1 }}
                    >
                      {emailSending ? 'Sending…' : 'Send Invoice'}
                    </button>
                    <button onClick={() => setShowEmailModal(false)} style={{ padding: '9px 18px', backgroundColor: 'var(--surface-muted)', border: '1px solid var(--border-color)', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>
                      Cancel
                    </button>
                  </div>
                </div>

                {/* Preview panel */}
                <div style={{ flex: 1, border: '1px solid var(--border-color)', borderRadius: 8, overflow: 'hidden', fontSize: 12 }}>
                  <div style={{ background: 'var(--accent-color)', padding: '16px', textAlign: 'center' }}>
                    <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>Avenues Unlimited Counseling Center</div>
                    <div style={{ color: '#d4eadb', fontSize: 11, marginTop: 2 }}>1774 Mellwood Ave, Louisville, KY 40206 &bull; 502.459.9635</div>
                  </div>
                  <div style={{ padding: '16px', background: '#fff' }}>
                    {emailMessage && (
                      <p style={{ whiteSpace: 'pre-wrap', marginBottom: 12, fontSize: 13 }}>{emailMessage}</p>
                    )}
                    {paidStatus && (
                      <div style={{ textAlign: 'center', marginBottom: 12 }}>
                        <span style={{ display: 'inline-block', color: '#41a62a', fontSize: 28, fontWeight: 'bold', border: '4px solid #41a62a', borderRadius: 6, padding: '2px 14px', opacity: 0.85, transform: 'rotate(-5deg)' }}>PAID</span>
                      </div>
                    )}
                    <div style={{ marginBottom: 10 }}>
                      <strong style={{ fontSize: 12 }}>Client:</strong> <span style={{ fontSize: 12 }}>{clientData?.clientName}</span>
                      {clientData?.clientDOB && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>DOB: {clientData.clientDOB}</span>}
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10, fontSize: 11 }}>
                      <thead>
                        <tr style={{ background: '#f2f2f2' }}>
                          {['Date','Service','Location','Diagnosis','Amount'].map(h => (
                            <th key={h} style={{ border: '1px solid var(--border-color)', padding: '5px 6px', textAlign: 'left' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sessions.map((s, i) => (
                          <tr key={i}>
                            <td style={{ border: '1px solid var(--border-color)', padding: '4px 6px' }}>{s.session_date ? String(s.session_date).split('T')[0] : ''}</td>
                            <td style={{ border: '1px solid var(--border-color)', padding: '4px 6px' }}>{s.service_type || ''}</td>
                            <td style={{ border: '1px solid var(--border-color)', padding: '4px 6px' }}>{s.location || 'Office'}</td>
                            <td style={{ border: '1px solid var(--border-color)', padding: '4px 6px' }}>{clientData?.diagnosisCode || ''}</td>
                            <td style={{ border: '1px solid var(--border-color)', padding: '4px 6px' }}>${parseFloat(s.billed_amount || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                        {customLineItems.map((item, i) => (
                          <tr key={'c' + i}>
                            <td style={{ border: '1px solid var(--border-color)', padding: '4px 6px' }}>{item.date || ''}</td>
                            <td style={{ border: '1px solid var(--border-color)', padding: '4px 6px' }}>{item.description || ''}</td>
                            <td style={{ border: '1px solid var(--border-color)', padding: '4px 6px' }}>{item.location || ''}</td>
                            <td style={{ border: '1px solid var(--border-color)', padding: '4px 6px' }}>{item.diagnosis || ''}</td>
                            <td style={{ border: '1px solid var(--border-color)', padding: '4px 6px' }}>${parseFloat(item.amount || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p style={{ textAlign: 'right', fontWeight: 700, fontSize: 13, margin: '0 0 10px 0' }}>Total: ${totalFees.toFixed(2)}</p>
                    {invoiceNotes && (
                      <div style={{ background: 'var(--surface-muted)', padding: '8px 10px', borderRadius: 4, fontSize: 11 }}>
                        <strong>Notes:</strong> {invoiceNotes}
                      </div>
                    )}
                  </div>
                  <div style={{ background: 'var(--surface-muted)', padding: '10px', textAlign: 'center', fontSize: 10, color: 'var(--text-muted)' }}>
                    Avenues Unlimited Counseling Center &bull; 1774 Mellwood Ave, Louisville, KY 40206 &bull; 502.459.9635 &bull; office@avenuesunlimited.com
                  </div>
                </div>

              </div>
            )}
          </div>
        </div>
      )}

      {sessions.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
          <h3>No Invoice Generated</h3>
          <p>Select a client and date range to generate an invoice.</p>
        </div>
      )}
    </div>
  );
}

export default AdminInvoicePage;